import { useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '@nexply/ui';
import { ImageCropDialog } from './ImageCropDialog.js';

interface Props {
  value: string;
  onChange: (dataUrl: string) => void;
  fallbackInitials?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASS = {
  sm: 'h-16 w-16 text-lg',
  md: 'h-20 w-20 text-xl',
  lg: 'h-24 w-24 text-2xl',
};

export function PhotoUpload({ value, onChange, fallbackInitials = '?', size = 'lg' }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErr('Nur Bilddateien werden unterstützt.');
      return;
    }
    setErr(null);
    setPending(file);
  }

  const sizeCls = SIZE_CLASS[size];

  return (
    <div className="flex items-center gap-4">
      {value ? (
        <img
          src={value}
          alt=""
          className={`${sizeCls} rounded-full object-cover border border-line`}
        />
      ) : (
        <div
          className={`${sizeCls} grid place-items-center rounded-full bg-gradient-to-br from-gold-light to-gold-dark text-[#1A1714] font-semibold`}
        >
          {fallbackInitials}
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileRef.current?.click()}
          >
            <Camera className="h-4 w-4 mr-1.5" />
            {value ? 'Ändern' : 'Foto hochladen'}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange('')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        {err && <div className="text-xs text-danger">{err}</div>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <ImageCropDialog
        file={pending}
        onCancel={() => setPending(null)}
        onConfirm={(dataUrl) => {
          onChange(dataUrl);
          setPending(null);
        }}
      />
    </div>
  );
}
